import {
  SHDotGroup,
  SHDotGroupProps,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { ConfigurationSection, HighlightType } from "@models/configuration";
import { ProductDataStatus } from "@models/product/enums/productDataStatus";
import { useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useState } from "react";

export interface ProductCollapseMenuProps {
  sections?: ConfigurationSection[];
  selectedIndex?: [number, number]; //[sectionIndex,subSectionIndex]
  onChange?: (index: [number, number]) => void;
  dotGroupProps?: Omit<SHDotGroupProps, "disabled">;
}
export const ProductCollapseMenu = ({
  sections,
  selectedIndex = [0, 0],
  onChange = (index) => {},
  dotGroupProps,
}: ProductCollapseMenuProps) => {
  const { palette } = useTheme();
  const [expandedIndex, setExpandedIndex] = useState(-1);
  return (
    <>
      {sections?.map((section, sectionIndex) => {
        const isExpanded = expandedIndex === sectionIndex;
        return (
          <SHCollapse
            key={section.id}
            title={
              <SHStack direction="row" spacing={0.5} alignItems="start">
                <SHTypography variant="subtitle1">{section.name}</SHTypography>
                <SHDotGroup
                  blueDot={
                    dotGroupProps?.blueDot &&
                    section.dataStatus === ProductDataStatus.MissingData
                  }
                  redDot={
                    dotGroupProps?.redDot &&
                    section.highlightType === HighlightType.Edited
                  }
                  paddingTop={"5px"}
                />
              </SHStack>
            }
            open={isExpanded}
            onCollapse={(open) => {
              setExpandedIndex(open ? sectionIndex : -1);
            }}
            width={"100%"}
            height="auto"
            sx={{
              padding: "20px 10px",
              cursor: "pointer",
              bgcolor: isExpanded ? palette.common.white : "transparent",
              borderColor: palette.secondary[100],
              borderBottomStyle: "solid",
              borderBottomWidth: isExpanded ? 0 : 1,
            }}
          >
            {section.subSections?.map(
              (subSection: ConfigurationSection, subSectionIndex) => {
                const selectedSubSection =
                  selectedIndex[0] === sectionIndex &&
                  selectedIndex[1] === subSectionIndex;
                return (
                  <SHStack
                    key={subSection.id}
                    sx={{
                      padding: "20px 10px",
                      cursor: "pointer",
                      bgcolor: selectedSubSection
                        ? hexToRGBA(palette.primary.light, 0.1)
                        : "transparent",
                      "&:hover": {
                        bgcolor: selectedSubSection
                          ? hexToRGBA(palette.primary.light, 0.15)
                          : hexToRGBA(palette.common.white, 0.5),
                      },
                    }}
                    onClick={() => {
                      onChange([sectionIndex, subSectionIndex]);
                    }}
                    direction="row"
                    spacing={0.5}
                    alignItems="start"
                  >
                    <SHTypography
                      variant={selectedSubSection ? "subtitle2" : "body1"}
                    >
                      {subSection.name}
                    </SHTypography>
                    <SHDotGroup
                      blueDot={
                        dotGroupProps?.blueDot &&
                        subSection.dataStatus === ProductDataStatus.MissingData
                      }
                      redDot={
                        dotGroupProps?.redDot &&
                        subSection.highlightType === HighlightType.Edited
                      }
                      paddingTop={"5px"}
                    />
                  </SHStack>
                );
              },
            )}
          </SHCollapse>
        );
      })}
    </>
  );
};
